import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { PAGE_SIZE } from '../productListHelper';

export const PlaceholderObserver = ({
  observerIndex,
  totalPlaceholders,
  onIntersect,
  groupId,
  children
}) => {
  const observerRef = useRef(null);
  const hasTriggeredRef = useRef(false);

  const shouldHaveObserver = observerIndex % PAGE_SIZE === 0;
  const batchNumber = Math.floor(observerIndex / PAGE_SIZE) + 1;

  const handleIntersection = useCallback((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        if (onIntersect) {
          onIntersect({
            groupId,
            observerIndex,
            batchNumber
          });
        }
      }
    });
  }, [observerIndex, onIntersect, groupId, batchNumber]);

  useEffect(() => {
    if (!shouldHaveObserver) {
      return undefined;
    }

    if (!observerRef.current) {
      return undefined;
    }

    const observerOptions = {
      root: null,
      rootMargin: '300px 0px 300px 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    observer.observe(observerRef.current);

    const checkViewport = () => {
      if (!observerRef.current || hasTriggeredRef.current) {
        return;
      }

      const rect = observerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;

      if (rect.height === 0) {
        requestAnimationFrame(() => {
          requestAnimationFrame(checkViewport);
        });
        return;
      }

      // Only trigger for first observer (index 0) if page is already scrolled
      // This handles the case where list loads mid-scroll
      // All other observers should trigger via IntersectionObserver
      const pageScrollY = window.scrollY || window.pageYOffset;
      const isFirstObserver = observerIndex === 0;
      const pageIsScrolled = pageScrollY > 0;

      if (isFirstObserver && pageIsScrolled && onIntersect && !hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        onIntersect({
          groupId,
          observerIndex,
          batchNumber
        });
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(checkViewport);
    });

    // Add scroll listener to check if observer was scrolled past without triggering
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!observerRef.current || hasTriggeredRef.current) {
          return;
        }

        const rect = observerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        // Check if observer is above the viewport (we scrolled past it)
        if (rect.bottom < 0) {
          hasTriggeredRef.current = true;
          if (onIntersect) {
            onIntersect({
              groupId,
              observerIndex,
              batchNumber
            });
          }
        }
      }, 150); // Debounce scroll events
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
      observer.disconnect();
    };
  }, [shouldHaveObserver, handleIntersection, groupId, observerIndex, batchNumber, totalPlaceholders, onIntersect]);

  return (
    <div data-testid="placeholder-observer-wrapper" className="sui-w-full">
      {/* Render the placeholder content */}
      {children}

      {/* Render observer div only on every 6th placeholder */}
      {shouldHaveObserver && (
        <div
          ref={observerRef}
          data-observer-group={groupId}
          data-observer-index={observerIndex}
          data-observer-batch={batchNumber}
          data-testid={`observer-div-${groupId}-${observerIndex}`}
          className="sui-h-1 sui-w-full"
          aria-hidden="true"
          style={{
            visibility: 'hidden',
            pointerEvents: 'none'
          }}
        />
      )}
    </div>
  );
};

PlaceholderObserver.displayName = 'PlaceholderObserver';

PlaceholderObserver.propTypes = {
  observerIndex: PropTypes.number.isRequired,
  totalPlaceholders: PropTypes.number.isRequired,
  onIntersect: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};
